import { useCallback } from "react";
import { toast } from "sonner";
import { useUpdateAgent } from "@/hooks/agent/useAgents";
import type { Agent } from "@/types/agent/agent";
import type { HubConfig } from "@/types/agent/info-links";

export interface UseHubConfigResult {
  /** The saved config, never null — an unconfigured Genie reads as `{}`. */
  config: HubConfig;
  /** Persist a slice of the config. Only the keys sent change. */
  save: (patch: Partial<HubConfig>, successMessage?: string) => Promise<void>;
  isSaving: boolean;
}

/**
 * Read/write access to a Genie's public page configuration.
 *
 * Config lives in its own column, `agents.hub_config`. Each card on the editor
 * owns one slice and calls {@link UseHubConfigResult.save} with only its own
 * fields; the API merges that patch over the stored object one level deep, so
 * a key's value — a list included — replaces the stored one whole. That is the
 * single merge on the save path: nothing is merged here, and nothing is deep-
 * merged on the server, so removing a featured question removes it.
 */
export const useHubConfig = (agent: Agent | undefined | null): UseHubConfigResult => {
  const updateAgent = useUpdateAgent();

  const config: HubConfig = agent?.hub_config ?? {};

  const save = useCallback(
    async (patch: Partial<HubConfig>, successMessage = "Page updated") => {
      if (!agent) return;

      await updateAgent.mutateAsync({
        agent,
        updateData: { hub_config: patch },
      });
      toast.success(successMessage);
    },
    [agent, updateAgent],
  );

  return { config, save, isSaving: updateAgent.isPending };
};
